import { Button, Group, Modal, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { removeAnalysis, type AnalysisStaff } from '@/api/analyses';
import { errorMessage } from '@/api/errors';

export interface RemoveAnalysisModalProps {
  analysis: AnalysisStaff;
  opened: boolean;
  onClose: () => void;
  onRemoved: () => void;
}

/** Confirm removing a planned analysis that was never used; the row disappears. */
export function RemoveAnalysisModal({ analysis, opened, onClose, onRemoved }: RemoveAnalysisModalProps) {
  const { t, i18n } = useTranslation('analyses');
  const { t: tc } = useTranslation();
  const qc = useQueryClient();
  const lang = i18n.language as 'cs' | 'en';
  const remove = useMutation({
    mutationFn: () => removeAnalysis(analysis.public_id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['analyses'] });
      void qc.invalidateQueries({ queryKey: ['project'] });
      void qc.invalidateQueries({ queryKey: ['projects'] });
      notifications.show({ color: 'forest', message: t('actions.deleted') });
      onClose();
      onRemoved();
    },
    onError: (err) => notifications.show({ color: 'red', message: errorMessage(err) }),
  });
  return (
    <Modal opened={opened} onClose={onClose} title={t('actions.delete')}>
      <Stack gap="md">
        <Text fw={600}>{analysis.name[lang] || analysis.name.cs}</Text>
        <Text fz={14}>{t('actions.deleteConfirm')}</Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {tc('common.cancel')}
          </Button>
          <Button color="red" loading={remove.isPending} onClick={() => remove.mutate()}>
            {tc('common.delete')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
